import React, { useEffect, useState } from 'react';
import { Alert, Avatar, Box, Button, CircularProgress, Container, Divider, Grid, IconButton, Paper, Snackbar, Stack, Typography, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import normalizeImageUrl from '../utils/normalizeImageUrl';
import { PREMIUM_FEATURES } from '../constants/premiumFeatures';

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [referralCopied, setReferralCopied] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [photoNotice, setPhotoNotice] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [referralBenefits, setReferralBenefits] = useState<any>({ rewards: [], rewardHistory: [], referralProgress: [], program: { referralRewardPercent: 5 } });
  const [referralBenefitsError, setReferralBenefitsError] = useState<string | null>(null);
  const referralCredits = PREMIUM_FEATURES.filter((feature) => Number(user?.referralDiscountCredits?.[feature.key]) > 0);

  useEffect(() => {
    if (!user?._id) return;
    let active = true;
    const loadReferralBenefits = async () => {
      try {
        const response = await api.get('/users/referral-benefits');
        if (!active) return;
        setReferralBenefits(response.data?.data || { rewards: [], rewardHistory: [], referralProgress: [], program: { referralRewardPercent: 5 } });
        setReferralBenefitsError(null);
      } catch (error: any) {
        console.error('Failed to load referral rewards', error);
        if (active) setReferralBenefitsError(error?.response?.data?.message || 'Referral reward details could not be loaded.');
      }
    };
    void loadReferralBenefits();
    return () => {
      active = false;
    };
  }, [user?._id]);

  const copyReferralCode = async () => {
    if (!user?.referralCode) return;
    try {
      await navigator.clipboard.writeText(user.referralCode);
      setReferralCopied(true);
      window.setTimeout(() => setReferralCopied(false), 1800);
    } catch {
      setReferralCopied(false);
    }
  };

  const handleProfilePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('File size must be less than 5MB');
      setPhotoNotice({ message: 'File size must be less than 5MB', severity: 'error' });
      return;
    }
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setPhotoError('Please upload a JPEG or PNG image');
      setPhotoNotice({ message: 'Please upload a JPEG or PNG image', severity: 'error' });
      return;
    }

    setUploadingPhoto(true);
    setPhotoError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadResponse = await api.post('/uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      const photoUrl = uploadResponse.data?.data?.fileUrl || uploadResponse.data?.fileUrl;
      if (!photoUrl) throw new Error('No file URL returned from upload');

      const response = await api.patch('/users', { profilePhoto: photoUrl }, { timeout: 60000 });
      if (response.data?.data) {
        updateUser(response.data.data);
      }
      setPhotoNotice({ message: 'Profile picture updated successfully', severity: 'success' });
    } catch (error: any) {
      const message = error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT'
        ? 'Profile picture upload timed out. Please try again on a stronger connection.'
        : error?.response?.data?.message || error?.message || 'Failed to upload profile picture';
      setPhotoError(message);
      setPhotoNotice({ message, severity: 'error' });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const sections = [
    {
      title: 'Academic profile',
      fields: [
        ['School / institution', user?.schoolName],
        ['School email', user?.schoolEmail],
        ['Education level', user?.educationLevel],
        ['Department', user?.department],
        ['Class', user?.className],
        ['Registration ID', user?.registrationId],
        ['JAMB subjects', user?.jambSubjects?.join(', ')],
        ['Purpose', user?.purpose],
      ],
    },
    {
      title: 'Parent / guardian',
      fields: [
        ['Name', user?.parentName],
        ['Email', user?.parentEmail],
        ['Phone', user?.parentPhone],
      ],
    },
    {
      title: 'Location',
      fields: [
        ['Address', user?.address],
        ['City', user?.city],
        ['Country', user?.country],
      ],
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 3, md: 5 } }}>
      <Container maxWidth="md">
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography variant="overline" color="text.secondary">Account</Typography>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>My Profile</Typography>
          <Typography color="text.secondary">Your account and student information.</Typography>
        </Stack>

        <Paper id="referral-code" component="section" elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 2.5, border: '1px solid', borderColor: 'divider' }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src={normalizeImageUrl(user?.profilePhoto) || user?.profilePhoto}
                  alt={user?.name || 'Profile'}
                  sx={{ width: 80, height: 80 }}
                >
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{user?.name || '—'}</Typography>
                  <Typography color="text.secondary">{user?.email || '—'}</Typography>
                </Box>
              </Stack>
              <Button component={Link} to="/membership" variant="outlined" sx={{ minHeight: 44 }}>Membership</Button>
            </Stack>
            <Divider />
            <Box>
              <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700, mb: 1 }}>Profile picture</Typography>
              {user?.googleId ? (
                <Typography variant="body2" color="text.secondary">
                  Your profile picture is automatically synced from your Google account.
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Upload a profile picture (Max 5MB, JPEG/PNG)
                  </Typography>
                  <Button component="label" variant="outlined" disabled={uploadingPhoto}>
                    {uploadingPhoto ? 'Uploading...' : 'Choose Picture'}
                    <input hidden accept="image/jpeg,image/png" type="file" onChange={handleProfilePhotoUpload} disabled={uploadingPhoto} />
                  </Button>
                  {uploadingPhoto && <CircularProgress size={24} sx={{ ml: 1, verticalAlign: 'middle' }} />}
                  {photoError && <Typography role="alert" variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>{photoError}</Typography>}
                </>
              )}
            </Box>
          </Stack>
        </Paper>

        <Paper component="section" elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 2.5, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 0.5 }}>Referral code</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {referralBenefits.program?.enabled === false
              ? 'Referral rewards are temporarily paused.'
              : `Share your code. Earn ${referralBenefits.program?.referralRewardPercent ?? 5}% referral rewards when a referred student pays for their 1st, 3rd, and 5th distinct premium features.`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {typeof user?.referralCount === 'number'
              ? `Used by ${user.referralCount} ${user.referralCount === 1 ? 'person' : 'people'}.`
              : 'Referral signup activity is loading.'}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" component="code" sx={{ px: 1.5, py: 0.75, bgcolor: 'action.hover', borderRadius: 1, letterSpacing: '0.08em' }}>
              {user?.referralCode || 'Loading code…'}
            </Typography>
            <Tooltip title={referralCopied ? 'Copied' : 'Copy referral code'}>
              <span>
                <IconButton aria-label="Copy referral code" onClick={copyReferralCode} disabled={!user?.referralCode}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          {referralBenefitsError && <Alert severity="error" sx={{ mt: 2 }}>{referralBenefitsError}</Alert>}
          {referralCredits.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Legacy feature discounts: {referralCredits.map((feature) => `${feature.label} (${user?.referralDiscountCredits?.[feature.key]})`).join(', ')}
            </Typography>
          )}
          {referralBenefits.rewards.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Available referral rewards</Typography>
              {referralBenefits.rewards.map((reward: any) => (
                <Typography key={reward._id} variant="body2" color="text.secondary">
                  {reward.discountPercent}% reward · milestone {reward.milestone} · expires {new Date(reward.expiresAt).toLocaleDateString()}
                </Typography>
              ))}
              <Button component={Link} to="/payment" variant="outlined" size="small" sx={{ mt: 1 }}>Use a reward</Button>
            </Box>
          )}
          {referralBenefits.referralProgress.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Referral progress</Typography>
              {referralBenefits.referralProgress.map((progress: any, index: number) => (
                <Typography key={`${progress.referredName}-${index}`} variant="body2" color="text.secondary">
                  {progress.referredName}: {progress.completedFeatures} distinct features paid
                  {progress.nextMilestone ? ` · next reward at ${progress.nextMilestone}` : ' · all milestones reached'}
                </Typography>
              ))}
            </Box>
          )}
          {referralBenefits.rewardHistory?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Reward history</Typography>
              {referralBenefits.rewardHistory.map((reward: any) => (
                <Typography key={reward._id} variant="body2" color="text.secondary">
                  {reward.discountPercent}% · milestone {reward.milestone} · {reward.status.toLowerCase()} · issued {new Date(reward.issuedAt).toLocaleDateString()}
                </Typography>
              ))}
            </Box>
          )}
        </Paper>

        <Grid container spacing={2.5}>
          {sections.map((section) => (
            <Grid item xs={12} sm={section.title === 'Academic profile' ? 12 : 6} key={section.title}>
              <Paper component="section" elevation={0} sx={{ height: '100%', p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 2 }}>{section.title}</Typography>
                <Stack spacing={1.5}>
                  {section.fields.map(([label, value]) => (
                    <Box key={label}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{value || '—'}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
      <Snackbar open={Boolean(photoNotice)} autoHideDuration={4000} onClose={() => setPhotoNotice(null)}>
        {photoNotice ? <Alert severity={photoNotice.severity} onClose={() => setPhotoNotice(null)} sx={{ width: '100%' }}>{photoNotice.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default Profile;